import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { NotificationService } from '../../../../../services/notification.service';
import { AddButtonComponent } from '../../../../../shared/components/add-button/add-button.component';
import { EmptyResultComponent } from '../../../../../shared/components/empty-result/empty-result.component';
import {
  LoadingTableShimmerComponent,
  ShimmerColumn,
} from '../../../../../shared/components/loading-table-shimmer/loading-table-shimmer.component';
import { ConfirmActionModalComponent } from '../../../../../shared/components/confirm-action-modal';
import { TunnelService } from '../../../../services/tunnel.service';
import { Tunnel, TunnelConnectionDisplay, TunnelPackage } from '../../../../models/tunnel.model';
import {
  apiErrorMessage,
  describeTunnelConnection,
  getTunnelTriggerModeLabel,
  tunnelDeleteMessage,
  tunnelPackageDeleteMessage,
} from '../../../../utils/tunnel.util';
import { TunnelFormModalComponent } from './tunnel-form-modal/tunnel-form-modal.component';
import { TunnelPackageFormModalComponent } from './tunnel-package-form-modal/tunnel-package-form-modal.component';

/**
 * Tunnel settings of one location (company detail → Location → Tunnel, CWS-1921):
 * the tunnel (Tunnel Controller device + trigger mode) and its tunnel wash packages
 * (one c-relay each). Which package starts which tunnel wash package is set in the
 * shop's package form.
 */
@Component({
  selector: 'app-location-tunnel',
  standalone: true,
  imports: [
    CommonModule,
    AddButtonComponent,
    EmptyResultComponent,
    LoadingTableShimmerComponent,
    ConfirmActionModalComponent,
    TunnelFormModalComponent,
    TunnelPackageFormModalComponent,
  ],
  templateUrl: './location-tunnel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./location-tunnel.component.scss'],
})
export class LocationTunnelComponent implements OnChanges, OnDestroy {
  @Input() companyId = '';
  @Input() locationId = '';
  /** Active tunnel wash packages, for the sub-tab badge. */
  @Output() activeCountChange = new EventEmitter<number>();

  loading = false;
  loadFailed = false;
  tunnel: Tunnel | null = null;
  tunnelPackages: TunnelPackage[] = [];
  connection: TunnelConnectionDisplay = describeTunnelConnection(null);
  triggerModeLabel = '';

  showTunnelForm = false;
  showDeleteTunnel = false;
  tunnelDeleteMessage = '';
  isDeletingTunnel = false;

  showPackageForm = false;
  editingPackage: TunnelPackage | null = null;
  packageToDelete: TunnelPackage | null = null;
  packageDeleteMessage = '';
  isDeletingPackage = false;

  readonly packageShimmerColumns: ShimmerColumn[] = [
    { label: 'Name', width: 'md' },
    { label: 'C-relay', width: 'sm' },
    { label: 'Status', width: 'sm' },
    { label: 'Packages', width: 'sm' },
  ];

  private readonly tunnelService = inject(TunnelService);
  private readonly messageService = inject(NotificationService);
  private loadSub?: Subscription;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyId'] || changes['locationId']) {
      this.closeAllModals();
      // Drop the previous location's tunnel at once: its controls must never act on
      // the newly selected location, and the loading state must show.
      this.loadFailed = false;
      this.applySettings(null, []);
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.loadSub?.unsubscribe();
  }

  /** `afterSave`: a refresh after a save, which keeps the screen if it fails. */
  load(options: { afterSave?: boolean } = {}): void {
    this.loadSub?.unsubscribe();
    if (!this.companyId || !this.locationId) {
      this.applySettings(null, []);
      return;
    }
    const companyId = this.companyId;
    const locationId = this.locationId;
    const afterSave = options.afterSave === true;
    this.loading = true;
    this.loadFailed = false;

    this.loadSub = this.tunnelService.getTunnelSettings(companyId, locationId).subscribe({
      next: response => {
        if (companyId !== this.companyId || locationId !== this.locationId) {
          return;
        }
        this.loading = false;
        if (!response.status || !response.data) {
          this.onLoadFailed(afterSave, response.message || 'Failed to load tunnel settings');
          return;
        }
        const data = response.data;
        this.applySettings(data.tunnel, data.tunnel_packages);
        this.activeCountChange.emit(data.counts.active_tunnel_packages);
      },
      error: err => {
        if (companyId !== this.companyId || locationId !== this.locationId) {
          return;
        }
        this.loading = false;
        this.onLoadFailed(afterSave, apiErrorMessage(err, 'Failed to load tunnel settings'));
      },
    });
  }

  /**
   * A refresh after a save keeps what is on screen and only reports the error; a
   * first load shows the retry state.
   */
  private onLoadFailed(isRefreshAfterSave: boolean, message: string): void {
    if (!isRefreshAfterSave) {
      this.loadFailed = true;
      this.applySettings(null, []);
    }
    this.notifyError(message);
  }

  private applySettings(tunnel: Tunnel | null, packages: TunnelPackage[]): void {
    this.tunnel = tunnel;
    this.tunnelPackages = packages;
    this.connection = describeTunnelConnection(tunnel?.sse_last_connected_at);
    this.triggerModeLabel = tunnel ? getTunnelTriggerModeLabel(tunnel.trigger_mode) : '';
  }

  // Tunnel

  onAddOrEditTunnel(): void {
    this.showTunnelForm = true;
  }

  onCloseTunnelForm(): void {
    this.showTunnelForm = false;
  }

  onTunnelSaved(): void {
    this.load({ afterSave: true });
  }

  onDeleteTunnel(): void {
    if (!this.tunnel) {
      return;
    }
    this.tunnelDeleteMessage = tunnelDeleteMessage(this.tunnel);
    this.showDeleteTunnel = true;
  }

  onCancelDeleteTunnel(): void {
    if (!this.isDeletingTunnel) {
      this.showDeleteTunnel = false;
    }
  }

  onConfirmDeleteTunnel(): void {
    if (this.isDeletingTunnel || !this.companyId || !this.locationId) {
      return;
    }
    const companyId = this.companyId;
    const locationId = this.locationId;
    this.isDeletingTunnel = true;
    this.tunnelService.deleteTunnel(companyId, locationId).subscribe({
      next: response => {
        this.isDeletingTunnel = false;
        if (!response.status) {
          this.notifyError(response.message || 'Failed to delete the tunnel');
          return;
        }
        this.showDeleteTunnel = false;
        this.notifySuccess('Tunnel deleted');
        if (companyId === this.companyId && locationId === this.locationId) {
          this.load();
        }
      },
      error: err => {
        this.isDeletingTunnel = false;
        this.notifyError(apiErrorMessage(err, 'Failed to delete the tunnel'));
      },
    });
  }

  // Tunnel wash packages

  onAddPackage(): void {
    this.editingPackage = null;
    this.showPackageForm = true;
  }

  onEditPackage(pkg: TunnelPackage): void {
    this.editingPackage = pkg;
    this.showPackageForm = true;
  }

  onClosePackageForm(): void {
    this.showPackageForm = false;
    this.editingPackage = null;
  }

  onPackageSaved(): void {
    this.load({ afterSave: true });
  }

  onDeletePackage(pkg: TunnelPackage): void {
    this.packageToDelete = pkg;
    this.packageDeleteMessage = tunnelPackageDeleteMessage(pkg);
  }

  onCancelDeletePackage(): void {
    if (!this.isDeletingPackage) {
      this.packageToDelete = null;
    }
  }

  onConfirmDeletePackage(): void {
    const pkg = this.packageToDelete;
    if (!pkg || this.isDeletingPackage || !this.companyId || !this.locationId) {
      return;
    }
    const companyId = this.companyId;
    const locationId = this.locationId;
    this.isDeletingPackage = true;
    this.tunnelService.deleteTunnelPackage(companyId, locationId, pkg.id).subscribe({
      next: response => {
        this.isDeletingPackage = false;
        if (!response.status) {
          this.notifyError(response.message || 'Failed to delete the tunnel wash package');
          return;
        }
        this.packageToDelete = null;
        this.notifySuccess('Tunnel wash package deleted');
        if (companyId === this.companyId && locationId === this.locationId) {
          this.load({ afterSave: true });
        }
      },
      error: err => {
        this.isDeletingPackage = false;
        this.notifyError(apiErrorMessage(err, 'Failed to delete the tunnel wash package'));
      },
    });
  }

  private closeAllModals(): void {
    this.showTunnelForm = false;
    this.showDeleteTunnel = false;
    this.showPackageForm = false;
    this.editingPackage = null;
    this.packageToDelete = null;
  }

  private notifySuccess(detail: string): void {
    this.messageService.add({ severity: 'success', summary: 'Success', detail });
  }

  private notifyError(detail: string): void {
    this.messageService.add({ severity: 'error', summary: 'Error', detail });
  }
}
